import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import * as Yup from 'yup';
import { Formik, useFormikContext } from 'formik';
import { Button, Grid } from '@material-ui/core';
import { FarmRiskType, INLAND_MARINE_RISK_NAME, lookupsJson } from '@ourbranch/lookups';

import {
  getPropertyTypeOptions,
  getMaskedResidenceUsageOptions,
  getMaskedRiskTypeOptions,
  getMaskedFO6StructureCategoryOptions,
  getFarmPolicyMask,
  applyInlandMarineExclusivity
} from 'common/helpers/farm-helpers';
import { useDisabledState } from 'common/disabled-context';
import { useClearInvalidRiskOptions } from 'common/helpers/use-clear-invalid-risk-options';
import { useFarmPolicyContext } from 'common/hooks/use-farm-policy-context';
import Field from 'core/components/form/form.v2';
import useSession from 'core/hooks/use-session';
import Label from 'core/components/label/label';
import InfoIcon from 'core/assets/svg/ic-info-dark.svg?react';
import { NotificationCard } from 'core/components/notification-card';
import {
  ValidationErrorsModal,
  useValidationErrorsModal,
  setTouchedFromErrors
} from 'core/components/validation-errors-modal';
import useStyles from './risk.styles';

// FO-4 property type options come from getPropertyTypeOptions (locked to FD "Home or Rental Unit")

const validationSchema = () =>
  Yup.object().shape({
    description: Yup.string().when('type', {
      // #33164: standalone inland marine is auto-named by InlandMarineDefaultsSetter — the agent never
      // types a name, so requiring one would make the risk unaddable.
      is: (value) => value === FarmRiskType.InlandMarine,
      then: (schema) => schema.nullable(),
      otherwise: (schema) => schema.required('Risk Name is required')
    }),
    type: Yup.string().required('Dwelling or structure type is required'),
    building: Yup.object().when('type', {
      is: (value) => [FarmRiskType.Fo1, FarmRiskType.Fo2, FarmRiskType.Fo3].includes(value),
      then: (schema) =>
        schema.shape({
          propertyType: Yup.string().required('Structure Category is required'),
          residenceUsage: Yup.string().required('Residence Type/Usage is required')
        }),
      otherwise: (schema) =>
        schema.shape({
          propertyType: Yup.string(),
          residenceUsage: Yup.string()
        })
    })
  });

// Auto-set FO4 defaults (locked fields) when risk type is selected — GitHub Issue: #29021
const FO4DefaultsSetter = () => {
  const { values, setFieldValue } = useFormikContext();

  useEffect(() => {
    if (values.type === FarmRiskType.Fo4) {
      if (values.building?.propertyType !== 'FD') {
        setFieldValue('building.propertyType', 'FD');
      }
      if (values.building?.residenceUsage !== 'C') {
        setFieldValue('building.residenceUsage', 'C');
      }
    }
  }, [values.type, setFieldValue]);

  return null;
};

// #33164: a standalone inland marine risk insures scheduled items, not a described structure, so
// there is nothing for the agent to name — auto-name it the way the FO-6 property types are named on
// the new-offer form. Mirrors FO4DefaultsSetter rather than hooking the select's onChange, so the name
// is also correct when the type is pre-set or the form remounts.
const InlandMarineDefaultsSetter = () => {
  const { values, setFieldValue } = useFormikContext();

  useEffect(() => {
    if (values.type === FarmRiskType.InlandMarine) {
      if (values.description !== INLAND_MARINE_RISK_NAME) {
        setFieldValue('description', INLAND_MARINE_RISK_NAME);
      }
    } else if (values.description === INLAND_MARINE_RISK_NAME) {
      // Switching away from inland marine must not leave the auto-name behind as a dwelling's name.
      setFieldValue('description', '');
    }
  }, [values.type, values.description, setFieldValue]);

  return null;
};

// Clear stale Structure Category / Residence Type/Usage values when the risk type changes,
// so e.g. Vacant picked on FO-1 isn't silently submitted for FO-2/FO-3 (#31692, mirrors #30827)
const InvalidRiskOptionClearer = () => {
  const { values, setFieldValue } = useFormikContext();
  useClearInvalidRiskOptions({
    riskType: values.type,
    propertyType: values.building?.propertyType,
    residenceUsage: values.building?.residenceUsage,
    setFieldValue,
    propertyTypePath: 'building.propertyType',
    residenceUsagePath: 'building.residenceUsage'
  });
  return null;
};

// Wrapper component to access parent Formik context and validation modal
const AddRiskWithValidation = observer(({ onAdd, locationAddress, disabled, fieldName }) => {
  const parentFormik = useFormikContext();
  const { showValidationErrorsModal, setShowValidationErrorsModal } = useValidationErrorsModal();

  return (
    <>
      <AddRiskInternal
        onAdd={onAdd}
        locationAddress={locationAddress}
        disabled={disabled}
        fieldName={fieldName}
        parentValues={parentFormik.values}
        parentValidateForm={parentFormik.validateForm}
        parentSetErrors={parentFormik.setErrors}
        parentSetTouched={parentFormik.setTouched}
        setShowValidationErrorsModal={setShowValidationErrorsModal}
      />
      <ValidationErrorsModal open={showValidationErrorsModal} onClose={() => setShowValidationErrorsModal(false)} />
    </>
  );
});

const DWELLING_RISK_TYPES = [FarmRiskType.Fo1, FarmRiskType.Fo2, FarmRiskType.Fo3];

const AddRiskInternal = observer(
  ({
    onAdd,
    locationAddress,
    disabled,
    fieldName,
    parentValues,
    parentValidateForm,
    parentSetErrors,
    parentSetTouched,
    setShowValidationErrorsModal
  }) => {
    const classes = useStyles();
    const { activeForm } = useDisabledState();
    // #32207: route-aware product + state. The offer store is empty on the policy route, so reading
    // the product from it there made getMaskedRiskTypeOptions fall back to full-Farm (F) and leak
    // farm-only risk types (scheduled/unscheduled farm property) + FO-6 structure categories onto
    // Home (Z) / Rental (Y). Prefer this location's own state; fall back to the policy/offer state.
    const { farmPolicyType, state: baseState } = useFarmPolicyContext();
    const session = useSession();

    const disableForm = Boolean(activeForm || disabled);

    const state = locationAddress?.state || baseState;
    const mask = getFarmPolicyMask(farmPolicyType, state);

    // Filter risk type options by policy type mask
    const baseRiskTypeOptions = lookupsJson.farm.riskType.filter(
      (option) => ![FarmRiskType.Fo6Scheduled, FarmRiskType.Fo6Unscheduled].includes(option.id)
    );
    // #33164: inland marine can only be quoted on its own and only by an underwriter, so narrow the
    // options by both — only inland marine once one exists, and otherwise only on a risk-less offer
    // for an underwriter. Filtering here makes the mixed state unreachable instead of merely
    // rejected later: the BE throws on a mixed payload, which surfaces as a hard error rather than a
    // fixable form message.
    const maskedRiskTypeOptions = applyInlandMarineExclusivity({
      riskTypeOptions: getMaskedRiskTypeOptions(farmPolicyType, state, baseRiskTypeOptions),
      farmLocations: parentValues?.farmLocations,
      isUnderwriter: Boolean(session.canApproveUWReview)
    });

    // Structure categories depend on state and policy type mask
    const allStructureCategories = state ? lookupsJson.farm.structureCategoriesByState[state] : [];
    const structureCategories = mask?.allowedFO6StructureCategories
      ? getMaskedFO6StructureCategoryOptions(farmPolicyType, state)
      : allStructureCategories;

    // No risk type is addable here, so there is no form to show. Reachable when the brand does not
    // opt into inland marine (getMaskedRiskTypeOptions subtracts it) but the offer holds one anyway
    // from a legacy or API-created payload — applyInlandMarineExclusivity then narrows to inland
    // marine alone and nothing is left. Rendering the form anyway gives a required Risk Type select
    // with no options and no way to satisfy it. (#33164)
    if (maskedRiskTypeOptions.length === 0) {
      return null;
    }

    return (
      <Formik
        initialValues={{
          description: '',
          type: undefined,
          propertyType: undefined,
          adjustmentFactor: 0,
          building: {
            buildingType: undefined,
            propertyType: undefined,
            residenceUsage: undefined,
            constructionType: undefined,
            numSolidFuelBurningAppliances: undefined
          }
        }}
        validateOnChange={false}
        validateOnBlur={false}
        validationSchema={validationSchema}
        onSubmit={async (values, actions) => {
          // Enforce dwelling risk count limit from the mask (e.g., Z allows max 1 dwelling)
          if (mask?.maxDwellingRisks != null && DWELLING_RISK_TYPES.includes(values.type)) {
            const existingDwellingCount = (parentValues?.farmLocations || []).reduce((count, loc) => {
              if (loc?.isDeleted) {
                return count;
              }
              return count + (loc?.risks || []).filter((r) => DWELLING_RISK_TYPES.includes(r?.type)).length;
            }, 0);
            if (existingDwellingCount >= mask.maxDwellingRisks) {
              actions.setStatus({
                error: `This policy type allows a maximum of ${mask.maxDwellingRisks} dwelling risk${mask.maxDwellingRisks === 1 ? '' : 's'} (FO-1, FO-2, or FO-3). Please remove an existing dwelling risk before adding a new one.`
              });
              return;
            }
          }

          // First validate the parent form (entire offer/policy)
          const parentErrors = await parentValidateForm();
          if (Object.keys(parentErrors).length > 0) {
            // Set errors and show validation modal
            parentSetErrors(parentErrors);
            // Convert errors object to touched structure (boolean flags instead of error messages)
            const touched = setTouchedFromErrors(parentErrors);
            parentSetTouched(touched, false);
            setShowValidationErrorsModal(true);
            return;
          }

          // If parent validation passes, proceed with location/risk-specific validation
          // Validate that location has required address fields
          if (!locationAddress?.city || !locationAddress?.state || !locationAddress?.zip) {
            actions.setStatus({
              error:
                'Location must have a complete address (at least City, State, and Zip) before adding risks. Please complete the location address first.'
            });
            return;
          }

          // For street addresses, also require street address
          if (
            locationAddress?.address === undefined &&
            values.type &&
            [
              FarmRiskType.Fo1,
              FarmRiskType.Fo2,
              FarmRiskType.Fo3,
              FarmRiskType.Fo4,
              FarmRiskType.Fo6Structures
            ].includes(values.type)
          ) {
            actions.setStatus({
              error:
                'This risk type requires a complete street address. Please ensure the location has a street address.'
            });
            return;
          }

          // Clear any previous errors
          actions.setStatus({});

          const riskToAdd = { ...values };
          // #33164: a standalone inland marine risk is building-less. Drop the empty `building`
          // scaffold this form initializes so the offer never carries a meaningless dwelling node.
          if (riskToAdd.type === FarmRiskType.InlandMarine) {
            delete riskToAdd.building;
          }
          onAdd(riskToAdd);
          actions.resetForm();
        }}
      >
        {({ values, submitForm, status }) => {
          return (
            <div className={classes.container}>
              <InvalidRiskOptionClearer />
              <InlandMarineDefaultsSetter />
              <Label type="infoSubtitleSmall">
                {values.type === FarmRiskType.InlandMarine
                  ? `Add ${INLAND_MARINE_RISK_NAME.toLowerCase()}`
                  : mask?.addRiskLabel || 'Add a dwelling or structure'}
              </Label>
              {status?.error && (
                <NotificationCard className={classes.notification} iconSize="icon16" type="errorRed">
                  <Label>{status.error}</Label>
                </NotificationCard>
              )}
              <Grid key="container" container justifyContent="space-between" alignItems="center" spacing={2}>
                <Field
                  xs={6}
                  type="select"
                  id={`${fieldName}-type`}
                  name="type"
                  mode="light"
                  label="Risk Type*"
                  options={maskedRiskTypeOptions}
                  disabled={disableForm}
                />
                <Field
                  xs={6}
                  type="string"
                  id={`${fieldName}-description`}
                  name="description"
                  mode="light"
                  // #33164: auto-named for inland marine, so neither editable nor required there.
                  label={values.type === FarmRiskType.InlandMarine ? 'Risk Name' : 'Risk Name*'}
                  disabled={disableForm || values.type === FarmRiskType.InlandMarine}
                />

                {values.type === FarmRiskType.Fo6Structures && locationAddress?.state && (
                  <Field
                    xs={12}
                    type="select"
                    id={`${fieldName}-building.buildingType`}
                    name="building.buildingType"
                    mode="light"
                    label="Structure Category"
                    options={structureCategories}
                    disabled={disableForm}
                  />
                )}

                {[FarmRiskType.Fo1, FarmRiskType.Fo2, FarmRiskType.Fo3].includes(values.type) && (
                  <>
                    {/* Keyed on values.type so the FastField remounts with the new type's
                        options when the risk type changes — otherwise stale options (e.g.
                        Vacant from FO-1) linger for FO-2/FO-3. Same fix as the edit modal
                        (#30827). GitHub issue #31692. */}
                    <Field
                      key={`${fieldName}-propertyType-${values.type}`}
                      xs={6}
                      type="select"
                      id={`${fieldName}.building.propertyType`}
                      name="building.propertyType"
                      mode="light"
                      label="Structure Category*"
                      options={getPropertyTypeOptions(values.type)}
                      disabled={disableForm}
                    />
                    <Field
                      key={`${fieldName}-residenceUsage-${values.type}`}
                      xs={6}
                      type="select"
                      id={`${fieldName}.building.residenceUsage`}
                      name="building.residenceUsage"
                      mode="light"
                      label="Residence Type/Usage*"
                      options={getMaskedResidenceUsageOptions(values.type, farmPolicyType, state)}
                      disabled={disableForm}
                    />
                  </>
                )}

                {values.type === FarmRiskType.Fo4 && <FO4DefaultsSetter />}

                {/* Address automatically inherits from location */}
                {values.type === FarmRiskType.Fo4 && (
                  <Grid item xs={12} className={classes.rentersInfoTextContainer}>
                    <InfoIcon width={16} height={16} />
                    <span className={classes.rentersInfoText}>
                      Make sure to include a floor, unit, or building number if one exists for this rental.
                    </span>
                  </Grid>
                )}
                <Grid item container justifyContent="flex-end" xs={12}>
                  <Button
                    type="button"
                    key="addButton"
                    mode="big"
                    variant="contained"
                    color="secondary"
                    className={classes.smallButton}
                    disabled={disableForm}
                    onClick={submitForm}
                  >
                    Add
                  </Button>
                </Grid>
              </Grid>
              {disableForm && (
                <NotificationCard className={classes.notification} iconSize="icon16" type="infoGreen">
                  <Label>You cannot add a risk until you update the offer.</Label>
                </NotificationCard>
              )}
            </div>
          );
        }}
      </Formik>
    );
  }
);

AddRiskWithValidation.propTypes = {
  onAdd: PropTypes.func.isRequired,
  locationAddress: PropTypes.shape({
    address: PropTypes.string,
    address2: PropTypes.string,
    city: PropTypes.string.isRequired,
    state: PropTypes.string.isRequired,
    zip: PropTypes.string.isRequired
  }),
  disabled: PropTypes.bool,
  fieldName: PropTypes.string.isRequired
};

AddRiskWithValidation.defaultProps = {
  disabled: false
};

AddRiskInternal.propTypes = {
  onAdd: PropTypes.func.isRequired,
  locationAddress: PropTypes.shape({
    address: PropTypes.string,
    address2: PropTypes.string,
    city: PropTypes.string.isRequired,
    state: PropTypes.string.isRequired,
    zip: PropTypes.string.isRequired
  }),
  disabled: PropTypes.bool,
  fieldName: PropTypes.string.isRequired,
  parentValues: PropTypes.object,
  parentValidateForm: PropTypes.func.isRequired,
  parentSetErrors: PropTypes.func.isRequired,
  parentSetTouched: PropTypes.func.isRequired,
  setShowValidationErrorsModal: PropTypes.func.isRequired
};

export default AddRiskWithValidation;
